<div class="content-card">
    <div class="card-inner">
        <p-fluid>
            <div class="font-semibold text-xl mb-4 module-name">
                Functional Area
            </div>

            <!-- Filters Row -->
            <div class="flex items-center justify-center gap-6 mb-4">
                <!-- Client Filter -->
                <div class="flex items-center gap-3">
                    <label for="clientFilter" class="font-medium whitespace-nowrap">Client:</label>
                    <div class="w-64">
                        <p-select id="clientFilter" [options]="viewModel.clientFilterOptions()"
                            [ngModel]="viewModel.selectedClientId()" (onChange)="viewModel.onClientChange($event)"
                            placeholder="Select Client" [showClear]="true" optionLabel="label" optionValue="value"
                            styleClass="w-full" [disabled]="viewModel.isClientDropdownDisabled()">
                        </p-select>
                    </div>
                </div>

                <!-- Test Type Filter -->
                <div class="flex items-center gap-3">
                    <label for="testTypeFilter" class="font-medium whitespace-nowrap">Test Type:</label>
                    <div class="w-64">
                        <p-select id="testTypeFilter" [options]="viewModel.testTypeFilterOptions()" 
                            [ngModel]="viewModel.selectedTestTypeFilter()"
                            placeholder="Filter by Test Type" 
                            [showClear]="!!viewModel.selectedTestTypeFilter()"
                            (onChange)="viewModel.filterByTestType($event.value)" 
                            (onClear)="viewModel.clearTestTypeFilter()"
                            styleClass="w-full">
                        </p-select>
                    </div>
                </div>
            </div>

            <p-table #dt [value]="viewModel.filteredFunctionalAreas() || []" [rows]="10" [paginator]="true"
                [globalFilterFields]="['name','description','package.packageName','testType.referenceValue']"
                [tableStyle]="{ 'min-width': '75rem' }" [rowHover]="true" dataKey="id"
                currentPageReportField="Showing {first} to {last} of {totalRecords} funtionalAreas"
                [showCurrentPageReport]="true" [rowsPerPageOptions]="[10, 20, 30]">

                <ng-template #caption>
                    <div class="flex items-center justify-between">
                        <h5 class="m-0"></h5>
                        <div class="flex flex-col items-end gap-3">
                            <p-button label="Add" icon="pi pi-plus" severity="info" *ngIf="viewModel.isSystemUser()"
                                (click)="viewModel.openNew()" />
                            <div class="search-container">
                                <span class="p-input-icon-left">
                                    <i class="pi pi-search"></i>
                                    <input pInputText type="text" (input)="viewModel.onGlobalFilter(dt, $event)"
                                        placeholder="Search..." />
                                </span>
                            </div>
                        </div>
                    </div>
                </ng-template>

                <ng-template #header>
                    <tr>
                        <th style="min-width:12rem">Package</th>
                        <th pSortableColumn="packageName" style="min-width:16rem">
                            Name
                            <p-sortIcon field="packageName" />
                        </th>
                        <th style="min-width: 16rem">
                            Test Type
                        </th>
                        <th style="min-width: 20rem">
                            Description
                        </th>
                        <th style="width: 12rem">Action</th>
                    </tr>
                </ng-template>

                <ng-template #body let-package>
                    <tr>
                        <td style="min-width: 12rem">
                            {{ package.package.packageName }}
                        </td>
                        <td style="min-width: 16rem">
                            <span class="package-name-link cursor-pointer text-blue-600 underline hover:text-blue-800"
                                (click)="viewModel.viewFunctionalArea(package)">
                                {{ package.name }}
                            </span>
                        </td>
                        <td style="min-width: 16rem">
                            {{ package.testType?.referenceValue || '-' }}
                        </td>
                        <td style="min-width: 20rem">
                            {{ package.description || '-' }}
                        </td>
                        <td style="width: 12rem">
                            <p-button icon="pi pi-pencil" severity="info" [rounded]="true" [outlined]="true"
                                [raised]="true" (click)="viewModel.editFunctionalArea(package)" pTooltip="Edit"
                                tooltipPosition="top" class="mr-2" />
                            <p-button icon="pi pi-trash" severity="danger" [rounded]="true" [outlined]="true"
                                [raised]="true" (click)="viewModel.deleteFunctionalArea(package)" pTooltip="Delete"
                                tooltipPosition="top" />
                        </td>
                    </tr>
                </ng-template>

                <ng-template #emptymessage>
                    <tr>
                        <td colspan="5" class="noData">
                            <ng-container *ngIf="!viewModel.selectedClientId() && !viewModel.getIsSystemUser()">
                                No functional areas found.
                            </ng-container>
                            <ng-container *ngIf="viewModel.selectedClientId() || viewModel.getIsSystemUser()">
                                No functional areas found.
                            </ng-container>
                        </td>
                    </tr>
                </ng-template>
            </p-table>
        </p-fluid>

        <!-- Package Dialog -->
        <p-dialog [(visible)]="viewModel.functionalAreaDialog" appendTo="body" [style]="{ width: '600px' }"
            [header]="viewModel.isViewMode ? 'Functional Area Details' : (viewModel.isEditMode ? 'Edit Functional Area' : 'Create Functional Area')"
            [modal]="true" [dismissableMask]="false" [blockScroll]="true" [draggable]="false"
            styleClass="module-name package-dialog" (onHide)="viewModel.hideDialog()">

            <ng-template pTemplate="content">
                <form [formGroup]="viewModel.form" class="p-fluid">
                    <!-- Package dropdown (Create only) -->
                    <div class="field">
                        <label for="packageId" class="block font-medium mb-2">Package <span
                                class="text-red-500">*</span></label>
                        <p-select id="packageId" formControlName="packageId" [options]="viewModel.packages"
                            placeholder="Select Package" class="w-full" appendTo="body"
                            [readonly]="viewModel.isViewMode"
                            [disabled]="viewModel.isViewMode">
                        </p-select>
                        <small class="text-red-500"
                            *ngIf="viewModel.form.get('packageId')?.invalid && viewModel.form.get('packageId')?.touched && !viewModel.isViewMode">
                            Package is required.
                        </small>
                    </div>

                    <!-- Suite name -->
                    <div class="field">
                        <label for="name" class="block font-medium mb-2">Name <span
                                class="text-red-500">*</span></label>
                        <input id="name" type="text" pInputText formControlName="name"
                            placeholder="Enter functional area name" class="w-full" [readonly]="viewModel.isViewMode" />
                        <small class="text-red-500"
                            *ngIf="viewModel.form.get('name')?.invalid && viewModel.form.get('name')?.touched && !viewModel.isViewMode">
                            Name is required.
                        </small>
                    </div>

                    <!-- Test Type after Suite name -->
                    <div class="field">
                        <label for="testTypeId" class="block font-medium mb-2">Test Type <span
                                class="text-red-500">*</span></label>
                        <p-select id="testTypeId" formControlName="testTypeId" [options]="viewModel.type_test()"
                            placeholder="Select Test type" class="w-full" appendTo="body"
                            [readonly]="viewModel.isViewMode" [disabled]="viewModel.isViewMode">
                        </p-select>
                        <small class="text-red-500"
                            *ngIf="viewModel.form.get('testTypeId')?.invalid && viewModel.form.get('testTypeId')?.touched && !viewModel.isViewMode">
                            Test Type is required.
                        </small>
                    </div>

                    <div class="field">
                        <label for="description" class="block font-medium mb-2">Description</label>
                        <textarea id="description" pTextarea formControlName="description"
                            placeholder="Enter test suite description" rows="3" class="w-full"
                            [readonly]="viewModel.isViewMode"></textarea>
                    </div>
                </form>
            </ng-template>

            <ng-template pTemplate="footer">
                <ng-container *ngIf="viewModel.isViewMode">
                    <p-button label="Close" icon="pi pi-times" severity="secondary"
                        (click)="viewModel.hideDialog()"></p-button>
                </ng-container>
                <ng-container *ngIf="!viewModel.isViewMode">
                    <p-button label="Cancel" icon="pi pi-times" severity="warn"
                        (click)="viewModel.hideDialog()"></p-button>
                    <p-button [label]="viewModel.isEditMode ? 'Update' : 'Submit'" icon="pi pi-check" severity="success"
                        (click)="viewModel.saveFunctionalArea()"></p-button>
                </ng-container>
            </ng-template>
        </p-dialog>

        <p-confirmdialog [style]="{ width: '450px' }" />
    </div>
</div>